import {
  A_NORMAL,
  startRogue
} from '../vendor/rogue-ts/dist/index.js';

const WIDTH = 80;
const HEIGHT = 24;

const blankGrid = () => Array.from({length: HEIGHT}, () => Array(WIDTH).fill(' '));

const formatC = (format, args) => {
  let result = '';
  let argIndex = 0;

  for (let i = 0; i < format.length; i += 1) {
    if (format[i] !== '%') {
      result += format[i];
      continue;
    }

    if (format[i + 1] === '%') {
      result += '%';
      i += 1;
      continue;
    }

    i += 1;
    let left = false;
    if (format[i] === '-') {
      left = true;
      i += 1;
    }

    let width = '';
    if (format[i] === '*') {
      width = Number(args[argIndex++]);
      i += 1;
    } else {
      while (/\d/.test(format[i])) {
        width += format[i];
        i += 1;
      }
      width = Number(width || 0);
    }

    const type = format[i];
    const value = args[argIndex++];
    let text = type === 'd' || type === 'i'
      ? String(Number(value))
      : type === 'c'
        ? String.fromCharCode(Number(value))
        : String(value === undefined ? '' : value);

    if (width > text.length) {
      const padding = ' '.repeat(width - text.length);
      text = left ? text + padding : padding + text;
    }
    result += text;
  }

  return result;
};

const keyCode = event => {
  const keys = {
    ArrowDown: 'j',
    ArrowUp: 'k',
    ArrowLeft: 'h',
    ArrowRight: 'l',
    Numpad1: 'b',
    Numpad2: 'j',
    Numpad3: 'n',
    Numpad4: 'h',
    Numpad5: '.',
    Numpad6: 'l',
    Numpad7: 'y',
    Numpad8: 'k',
    Numpad9: 'u',
    Home: 'y',
    End: 'n',
    Enter: 13,
    Escape: 27,
    Backspace: 8,
    Tab: 9,
    ' ': 32
  };

  if (keys[event.key] !== undefined) {
    return typeof keys[event.key] === 'string'
      ? keys[event.key].charCodeAt(0)
      : keys[event.key];
  }
  if (event.key && event.key.length === 1) return event.key.charCodeAt(0);
  return event.which || event.keyCode || 0;
};

const createBackend = render => {
  const grid = blankGrid();
  const windows = new Map();
  let nextWindowId = 1;
  let cursorY = 0;
  let cursorX = 0;
  const queue = [];
  const waiters = [];

  const windowFor = win => win ? windows.get(win.id) : {
    id: 0,
    rows: HEIGHT,
    cols: WIDTH,
    y: 0,
    x: 0
  };

  const put = (y, x, ch) => {
    if (y >= 0 && y < HEIGHT && x >= 0 && x < WIDTH) {
      grid[y][x] = ch;
    }
  };

  const move = (y, x) => {
    cursorY = Math.max(0, Math.min(HEIGHT - 1, y));
    cursorX = Math.max(0, Math.min(WIDTH - 1, x));
  };

  const addch = ch => {
    put(cursorY, cursorX, String.fromCharCode(ch & 0xff));
    if (cursorX < WIDTH - 1) cursorX += 1;
  };

  const addstr = text => String(text).split('').forEach(ch => addch(ch.charCodeAt(0)));

  const mvaddch = (y, x, ch) => {
    move(y, x);
    addch(ch);
  };

  const mvaddstr = (y, x, text) => {
    move(y, x);
    addstr(text);
  };

  const clearRect = win => {
    const target = windowFor(win);
    for (let y = 0; y < target.rows; y += 1) {
      for (let x = 0; x < target.cols; x += 1) {
        put(target.y + y, target.x + x, ' ');
      }
    }
  };

  const wmove = (win, y, x) => {
    const target = windowFor(win);
    move(target.y + y, target.x + x);
  };

  const waddch = (win, ch) => addch(ch);
  const waddstr = (win, text) => addstr(text);
  const mvwaddch = (win, y, x, ch) => {
    wmove(win, y, x);
    waddch(win, ch);
  };
  const mvwaddstr = (win, y, x, text) => {
    wmove(win, y, x);
    waddstr(win, text);
  };

  const newwin = (rows, cols, y, x) => {
    const win = {id: nextWindowId++, rows, cols, y, x};
    windows.set(win.id, win);
    return win;
  };

  const enqueue = code => {
    const waiter = waiters.shift();
    if (waiter) waiter(code);
    else queue.push(code);
  };

  return {
    LINES: HEIGHT,
    COLS: WIDTH,
    initscr: () => ({id: 0}),
    endwin: () => {},
    isendwin: () => false,
    move,
    addch,
    addstr,
    mvaddch,
    mvaddstr,
    printw: (format, ...args) => addstr(formatC(format, args)),
    mvprintw: (y, x, format, ...args) => mvaddstr(y, x, formatC(format, args)),
    inch: () => grid[cursorY][cursorX].charCodeAt(0),
    mvinch: (y, x) => grid[y]?.[x]?.charCodeAt(0) || 32,
    clear: () => {
      for (let y = 0; y < HEIGHT; y += 1) grid[y].fill(' ');
      move(0, 0);
    },
    clrtoeol: () => {
      for (let x = cursorX; x < WIDTH; x += 1) put(cursorY, x, ' ');
    },
    refresh: render,
    standout: () => {},
    standend: () => {},
    getyx: () => [cursorY, cursorX],
    newwin,
    delwin: win => windows.delete(win.id),
    wmove,
    waddch,
    mvwaddch,
    waddstr,
    mvwaddstr,
    wprintw: (win, format, ...args) => waddstr(win, formatC(format, args)),
    wrefresh: render,
    wclear: win => clearRect(win),
    wclrtoeol: () => {
      for (let x = cursorX; x < WIDTH; x += 1) put(cursorY, x, ' ');
    },
    touchwin: () => {},
    clearok: () => {},
    overwrite: () => {},
    getch: () => queue.length
      ? Promise.resolve(queue.shift())
      : new Promise(resolve => waiters.push(resolve)),
    enqueue,
    cbreak: () => {},
    nocbreak: () => {},
    noecho: () => {},
    echo: () => {},
    raw: () => {},
    noraw: () => {},
    keypad: () => {},
    typeahead: () => {},
    baudrate: () => 9600,
    mvcur: () => {},
    idlok: () => {},
    saveGame: async () => true,
    restoreGame: async () => null,
    getText: () => grid.map(line => line.join('')).join('\n'),
    reset: () => {
      queue.length = 0;
      waiters.length = 0;
    }
  };
};

export const launchRogue = async terminal => {
  const root = document.querySelector('#terminal');
  const textarea = root.querySelector('textarea');
  const screen = document.createElement('pre');
  screen.className = 'rogue-screen';
  screen.tabIndex = 0;
  screen.style.cssText = [
    'position:absolute',
    'inset:0',
    'margin:0',
    'padding:18px',
    'overflow:hidden',
    'color:inherit',
    "font-family:'Topaz A1200',monospace",
    'font-size:32px',
    'line-height:1',
    'white-space:pre',
    'text-shadow:inherit',
    'outline:0',
    'z-index:60'
  ].join(';');

  const render = () => {
    screen.textContent = backend.getText();
  };
  const backend = createBackend(render);
  const fitScreen = () => {
    const availableWidth = Math.max(320, root.clientWidth - 36);
    const availableHeight = Math.max(240, root.clientHeight - 36);
    const widthSize = availableWidth / (WIDTH * 0.6);
    const heightSize = availableHeight / HEIGHT;
    const size = Math.max(16, Math.min(32, Math.floor(Math.min(widthSize, heightSize))));
    screen.style.fontSize = `${size}px`;
  };
  const onKeyDown = event => {
    event.preventDefault();
    event.stopPropagation();
    backend.enqueue(keyCode(event));
  };

  root.appendChild(screen);
  fitScreen();
  window.addEventListener('resize', fitScreen);
  textarea.style.display = 'none';
  screen.addEventListener('keydown', onKeyDown);
  screen.focus();

  try {
    await startRogue(backend, {playerName: 'SLUMGUD'});
  } catch (error) {
    console.error(error);
    terminal.print(`Rogue error: ${error.message || error}`);
  } finally {
    backend.reset();
    window.removeEventListener('resize', fitScreen);
    screen.removeEventListener('keydown', onKeyDown);
    screen.remove();
    textarea.style.display = '';
    terminal.focus();
  }
};
